import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Card from 'src/components/common/Card.redesign';
import CardContainer from 'src/components/common/CardContainer.redesign';
import Dialog from 'src/components/common/Dialog';
import Filter from 'src/components/common/Filter';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import RHFTextArea from 'src/components/controls/tempRHFControls/RHFTextArea';
import RHFToggle from 'src/components/controls/tempRHFControls/RHFToggle';
import Company from 'src/models/Company';
import Invoice from 'src/models/leasing/Invoice';
import InvoiceGroup from 'src/models/leasing/InvoiceGroup';
import State from 'src/models/State';
import { ODataOptions } from 'src/util/ODataBuilder';
import useFetch from 'use-http';
import * as _ from 'lodash';

interface IManageInvoiceGroupsDialogStateProps {
    dialogName: string;
    clientId: number;
    projectId: number;
    invoiceGroups: InvoiceGroup[];
    companies: Company[];
    states: State[];
    formatData: (data: InvoiceGroup) => InvoiceGroup;
    hideDialog: (reset: boolean) => void;
}

const ManageInvoiceGroupsDialog = (props: IManageInvoiceGroupsDialogStateProps) => {
    const bonusOptionValues = [
        {value: 'Bonus', label: 'Bonus'},
        {value: 'Option', label: 'Option'}
    ];

    const defaultValues: InvoiceGroup = {
        id: 0,
        groupNumber: '',
        clientId: props.clientId,
        companyId: 0,
        stateId: 0,
        clientName: '',
        clientAddress: '',
        salutation: null,
        signature: '',
        signatureTitle: '',
        landmanName: '',
        city: '',
        zipCode: '',
        letterText: '',
        isCoverLetter: null,
        printPageNumber: null,
        sectionOnInvoice: null,
        bonusOption: '',
        netAcres: 0,

        invoices: [],
    };

    const [invoiceGroupData, setInvoiceGroupData] = useState<InvoiceGroup>(defaultValues);
    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const [selectedEntities, setSelectedEntities] = useState<boolean[]>([]);
    const [filters, setFilters] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [invoiceGroupIds, setInvoiceGroupIds] = useState<string[]>(['']);

    const { register, handleSubmit, getValues, reset, errors } = useForm();
    const { get, put, post, response } = useFetch();

    useEffect(() => {
        loadInvoices();
        setSelectedEntities([]);
    }, [filters, props.clientId]);
    
    useEffect(() => {
        reset(invoiceGroupData);
    }, [invoiceGroupData]);

    useEffect(() => {
        updateInvoiceGroupOptions();
    }, [props.invoiceGroups]);

    useEffect(() => {
        updateSelectedEntities();
    }, [invoices]);
    
    useEffect(() => {
        displayInvoices();
    }, [selectedEntities]);

    const loadInvoices = async () => {
        // Load withholding invoices
        if (props.clientId && props.clientId > 0) {
            let invoiceGroupFilter;
            if (filters.length > 0) {
                invoiceGroupFilter = filters + ` and where eq clientId,${props.clientId}`;
            } else {
                invoiceGroupFilter = `?$filter=where eq clientId,${props.clientId}`;
            }

            let data: Invoice[] = await get(`v1.0/clients/${props.clientId}/projects/${props.projectId}/invoice${invoiceGroupFilter}`);
    
            if (response.ok) {
                setInvoices(data);
            } else {
                console.log('bad request', response, filters);
            }
        } else if (filters.length === 0) {
            setInvoices([]);
        }
    };

    const updateInvoiceGroupOptions = () => {
        let ids = ['', '--CREATE--'];

        if (props.invoiceGroups?.length > 0) {
            props.invoiceGroups.forEach(ig => {
                ids.push(ig.groupNumber);
            });
        }

        setInvoiceGroupIds(ids);
    };

    const updateSelectedEntities = () => {
        let entityList: boolean[] = [];
        invoices.forEach(() => {
            entityList.push(false);
        });
        setSelectedEntities(entityList);
    };

    const displayInvoiceGroupForm = () => {
        let newEntities = selectedEntities;
        let rec = getValues();

        if (rec.groupNumber === '--CREATE--') {
            setInvoiceGroupData(defaultValues);
        } else {
            let index = props.invoiceGroups.indexOf(props.invoiceGroups.find(ig => ig.groupNumber === rec.groupNumber));

            if (index > -1) {
                setInvoiceGroupData(props.invoiceGroups[index]);
                let currentInvoices = props.invoiceGroups[index]?.invoices;
                currentInvoices.forEach(inv => {
                    let invIndex = currentInvoices.indexOf(currentInvoices.find(cinv => cinv.id === inv.id));
                    newEntities[invIndex] = true;
                });
            }
        }

        setSelectedEntities(newEntities);
        setShowForm(true);
    };

    const toggleSelectedEntities = (key: number) => {
        let newEntities = [...selectedEntities];
        newEntities[key] = !newEntities[key];

        setSelectedEntities(newEntities);
    };

    const updateFilter = (options: ODataOptions) => {
        const stringOptions = options.toString();
        searchUpdate(stringOptions);
    };

    const searchUpdate = useCallback(
        _.debounce((filter: string) => {
            setFilters(filter);
        }, 800),
        []
    );

    const displayInvoices = () => {
        if (invoices?.length > 0) {
            return invoices?.map(((inv, key) => {
                let cardClass = selectedEntities[key] === true ? ' selected' : '';

                return (
                    <Card className={`small-input${cardClass}`} newContext={false} onClick={() => toggleSelectedEntities(key)}>
                        <div style={{ display: 'flex' }}>
                            <div className="mr-2" style={{ width: '100%' }}>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ padding: '1rem', width: '25%' }}>
                                        <div>Invoice Number: {inv.invoiceNumber ? inv.invoiceNumber : 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '25%' }}>
                                        <div>Lease Number: {inv.leaseNumber ? inv.leaseNumber : 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '20%' }}>
                                        <div>Project Number: {inv.projectNumber ? inv.projectNumber : 'N/A'}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '15%' }}>
                                        <div>Bonus: {inv.bonusAmount}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '15%' }}>
                                        <div>Rental: {inv.rentAmount}</div>
                                    </div>
                                </div>
                                <div style={{ display: 'flex' }}>
                                    <div style={{ padding: '1rem', width: '20%' }}>
                                        <div>Bank Fees: {inv.bankCharge}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '20%' }}>
                                        <div>Rec. Fees: {inv.recordingFee}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '20%' }}>
                                        <div>Other Fees: {inv.otherFee}</div>
                                    </div>
                                    <div style={{ padding: '1rem', width: '40%' }}>
                                        <div>Lessor Name: {inv.lessorName ? inv.lessorName : 'N/A'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Card>
                );
            }));
        } else {
            return <div key="0">No Current Records</div>;
        }
    };

    const onSubmit = async (invoiceGroup: InvoiceGroup) => {
        invoiceGroup = props.formatData(invoiceGroup);
        invoiceGroup.id = invoiceGroupData?.id;

        invoiceGroup.invoices = [];
        selectedEntities.forEach((se, key) => {
            if (se === true) {
                let invoice = invoices[key];
                invoiceGroup.invoices.push(invoice);
            }
        });

        if (invoiceGroup.id > 0) {
            await put(`v1.0/clients/${props.clientId}/projects/${props.projectId}/invoice/invoice-group`, invoiceGroup);
        } else {
            await post(`v1.0/clients/${props.clientId}/projects/${props.projectId}/invoice/invoice-group`, invoiceGroup);
        }
        if (response.ok) {
            props.hideDialog(true);
        }
    };

    const onCloseDialog = () => {
        setShowForm(false);
        setInvoiceGroupData(defaultValues);
        setInvoices([]);
        setSelectedEntities([]);
        setFilters('');
        setInvoiceGroupIds(['']);
    };

    return (
        <Dialog dialogName={props.dialogName} title={`Manage Invoice Groups`} onCloseDialog={onCloseDialog}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="mr-2">
                    <div style={{ display: 'flex' }}>
                        <div style={{ padding: '1rem' }}>
                            <RHFSelect
                                name="groupNumber"
                                label="Invoice Group #"
                                options={invoiceGroupIds?.map((ig) => {
                                    return { value: ig, label: ig };
                                })}
                                register={register}
                                errors={errors}
                                onChange={displayInvoiceGroupForm}
                            />
                        </div>
                    </div>
                </div>
                {showForm && (
                    <div style={{ minHeight: '10rem', maxHeight: '80rem', overflow: 'scroll' }}>
                        <div style={{ display: 'flex' }}>
                            <div style={{ width: '50%', padding: '1rem' }}>
                                <div className="form-row">
                                    <RHFInputBox name="groupDate" label="Group Date" register={register} errors={errors} type="date" />
                                </div>
                            </div>
                            <div style={{ width: '50%', padding: '1rem' }}>
                                <div className="form-row">
                                    <RHFSelect
                                        name="companyId"
                                        label="Company"
                                        options={props.companies?.map(c => {
                                            return { value: c.id, label: c.name };
                                        })}
                                        register={register}
                                        style={{ width: '100%' }}
                                    />
                                    <RHFInputBox name="clientNumber" label="Client #" register={register} errors={errors} />
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex' }}>
                            <div style={{ width: '50%', padding: '1rem' }}>
                                <RHFInputBox name="clientName" label="Client Name" register={register} errors={errors} style={{ width: '100%' }} />
                                <RHFTextArea name="clientAddress" label="Client Address" register={register} errors={errors} />
                            </div>
                            <div style={{ width: '50%', padding: '1rem' }}>
                                <RHFInputBox name="salutation" label="Salutation" register={register} errors={errors} style={{ width: '100%' }} />
                                <RHFInputBox name="signature" label="Signature" register={register} errors={errors} style={{ width: '100%' }} />
                                <RHFInputBox name="signatureTitle" label="Signature Title" register={register} errors={errors} style={{ width: '100%' }} />
                                <RHFInputBox name="landmanName" label="Landman" register={register} errors={errors} style={{ width: '100%' }} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', padding: '1rem' }}>
                            <div className="form-row">
                                <RHFInputBox name="city" label="City" register={register} errors={errors} style={{ width: '100%' }} />
                                <RHFSelect
                                    name="stateId"
                                    label="State"
                                    options={props.states?.map(st => {
                                        return { value: st.id, label: st.name };
                                    })}
                                    register={register}
                                    style={{ width: '100%' }}
                                />
                                <RHFInputBox name="zipCode" label="Zip Code" register={register} errors={errors} />
                            </div>
                        </div>
                        <div style={{ display: 'flex', padding: '1rem' }}>
                            <RHFTextArea name="letterText" label="Letter Text" register={register} errors={errors} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem' }}>
                            <RHFToggle name="isCoverLetter" label="Cover Letter?" register={register} errors={errors} />
                            <RHFToggle name="printPageNumber" label="Print Page Number?" register={register} errors={errors} />
                            <RHFSelect
                                name="bonusOption"
                                label="Bonus or Option?"
                                options={bonusOptionValues}
                                register={register}
                                style={{ width: '100%' }}
                            />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem' }}>
                            <RHFInputBox name="netAcres" label="Net Acres" register={register} errors={errors} type="number" />
                            <RHFToggle name="sectionOnInvoice" label="Print Section on Rec. Fee Invoice?" register={register} errors={errors} />
                        </div>

                        {/* Lease Attachment Cards */}
                        <div style={{ width: '100%' }}>
                            <Filter
                                filterItems={[
                                    { group: 1, text: 'Lease Number', orderName: 'leaseNumber', orderDirection: 'descending' },
                                    { group: 1, text: 'Invoice Number', orderName: 'invoiceNumber', orderDirection: 'descending' },
                                    { group: 1, text: 'Project Number', orderName: 'projectNumber', orderDirection: 'descending' },
                                    { group: 1, text: 'Created After Date', orderName: 'createdAfter', orderDirection: 'descending', type: 'date' },
                                    { group: 1, text: 'Created Before Date', orderName: 'createdBefore', orderDirection: 'descending', type: 'date' },
                                ]}
                                onFilterChanged={updateFilter}
                            />
                            <div style={{ display: 'flex' }}>
                                <div className="mr-2" style={{ width: '100%' }}>
                                    Invoices
                                </div>
                            </div>
                            <CardContainer showNextPagePanel={false}>
                                <div style={{ minHeight: '10rem', maxHeight: '30rem', overflow: 'scroll' }}>{displayInvoices()}</div>
                            </CardContainer>
                        </div>
                        <div>
                            <Button text="Submit" intent="primary" type="submit" />
                        </div>               
                    </div>
                )}
            </form>
        </Dialog>
    );
};

export default ManageInvoiceGroupsDialog;


// WEBPACK FOOTER //
// ./src/components/invoicing/components/ManageInvoiceGroupsDialog.tsx